import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Form as FormAnt } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ActionButton } from "@hdi-seguros/web-components.action-button";

import { formatDate, ValidateForm, validateCuil } from "../../../../../shared/utils";

import "./index.css";
import {
  ColSize,
  RowSize,
  Form,
  InputForm,
  SelectForm,
  DatePickerForm,
} from "../../../../../shared/components";
import { MasterContext } from "../../../../../shared/contexts";
import QuotationService from "../../../../../shared/services/quotation";
import { AgeRange, InsurancePolicy, SelectOption } from "../../../../../shared/interfaces";
import dayjs, { Dayjs } from "dayjs";

interface InsuredRegistrationFormProps {
  onSubmit: (formFields: any) => void;
  onGenerate: () => void;
  insurancePolicyDetail?: InsurancePolicy;
  policyProductCode?: string;
  disabledButtons: boolean;
}

export const InsuredRegistrationForm: FC<InsuredRegistrationFormProps> = ({
  onSubmit,
  insurancePolicyDetail,
  policyProductCode,
  onGenerate,
  disabledButtons
}) => {
  const [form] = FormAnt.useForm<any>();
  const validateForm = new ValidateForm();
  const [apActiviesListOptions, setApActivitiesListOptions] = useState<Array<SelectOption>>([]);
  const [ageRanges, setAgeRanges] = useState<Array<AgeRange>>([]);
  const [isFormComplete, setIsFormComplete] = useState<boolean>(false);

  const extractDocumentNumberFromCuil = (cuil: string): string => {
    if (!cuil) return '';

    const cleanCuil = cuil.replace(/[- ]/g, '');

    if (cleanCuil.length !== 11) return '';

    return cleanCuil.substring(2, 10);
  };

  const getDocumentTypeFromCuil = (cuil: string): string => {
    if (!cuil) return '';

    const cleanCuil = cuil.replace(/[- ]/g, '');

    if (cleanCuil.length !== 11) return '';

    const personType = cleanCuil.substring(0, 2);

    if (['20', '23', '24', '27'].includes(personType)) {
      return 'DNI';
    }

    return 'CUIT';
  };

  const handleCuilChange = (value: string) => {
    if (validateCuil(value)) {
      const documentNumber = extractDocumentNumberFromCuil(value);
      const documentType = getDocumentTypeFromCuil(value);

      form.setFieldValue('documentNumber', documentNumber);

      if (documentType === 'DNI') {
        const dniOption = documentTypeList?.find(option =>
          option.value?.toLowerCase().includes("4")
        );

        if (dniOption) {
          form.setFieldValue('documentType', dniOption.value);
        }
      }
    }
    // Validar formulario después del cambio
    setTimeout(validateFormComplete, 100);
  };

  // Función para validar si todos los campos están completos
  const validateFormComplete = () => {
    const formValues = form.getFieldsValue();
    const requiredFields = ['fullName', 'documentType', 'documentNumber', 'birthDate', 'gender', 'activities'];

    const isComplete = requiredFields.every(field => {
      const value = formValues[field];
      return value !== undefined && value !== null && value !== '';
    });

    setIsFormComplete(isComplete);
  };

  const {
    documentTypeList,
    genderList,
    getGenderList,
    isLoadingDocumentTypeList,
    isLoadingGenderList,
    getDocumentTypeList,
  } = useContext(MasterContext);

  const onFinish = (formFields: any) => {
    delete formFields[""];

    // Validar que birthDate sea válido antes de formatearlo
    if (formFields["birthDate"] && dayjs(formFields["birthDate"]).isValid()) {
      formFields["birthDate"] = dayjs(formFields["birthDate"]).format('DD-MM-YYYY');
    }
    onSubmit(formFields);
    setTimeout(validateFormComplete, 100)
    form.resetFields();
  };
  const getAPActiviesListPerProduct = async (
    article: string,
    branch: string,
    product: string
  ) => {
    try {
      const response = await QuotationService.getAPActivitiesPerProduct(
        article,
        branch,
        product
      );

      if (response.data?.actividadesApPorProducto?.length) {
        const activities = response.data.actividadesApPorProducto.map(
          ({ codigo, descripcion }) => ({ label: descripcion, value: codigo })
        );
        setApActivitiesListOptions(activities);
      } else {
        setApActivitiesListOptions([]);
      }
    } catch (error) {
      setApActivitiesListOptions([]);
    }
  };

  const getAgeRangesPerProduct = useCallback(async (
    article: string,
    branch: string,
    product: string
  ) => {
    try {
      const response = await QuotationService.getAgeRangeList(article, branch, product);
      setAgeRanges(response?.data?.rangosDeEdad ?? []);
    } catch {
      setAgeRanges([]);
    }
  }, []);

  const ageLimits = useMemo(() => {
    if (!ageRanges.length) return { minDate: undefined, maxDate: dayjs().subtract(1, 'day') };

    const minAllowedAge = Math.min(...ageRanges.map((item) => Number(item.edadMinima)));
    const maxAllowedAge = Math.max(...ageRanges.map((item) => Number(item.edadMaxima)));

    if (Number.isNaN(minAllowedAge) || Number.isNaN(maxAllowedAge)) {
      return { minDate: undefined, maxDate: dayjs().subtract(1, 'day') };
    }

    const maxDateByAge = dayjs().subtract(minAllowedAge, 'year');
    const maxDate = maxDateByAge.isAfter(dayjs().subtract(1, 'day'))
      ? dayjs().subtract(1, 'day')
      : maxDateByAge;
    const minDate = dayjs().subtract(maxAllowedAge, 'year');

    return { minDate, maxDate };
  }, [ageRanges]);

  const validateBirthDateRange = useCallback((_: unknown, value: Dayjs) => {
    if (!value) return Promise.resolve();
    if (!ageLimits.minDate || !ageLimits.maxDate) {
      return Promise.reject(
        new Error('No se pudo validar el rango de edad para el producto de la póliza.')
      );
    }
    const selected = dayjs(value);
    if (selected.isBefore(ageLimits.minDate, 'day') || selected.isAfter(ageLimits.maxDate, 'day')) {
      return Promise.reject(
        new Error(
          `La fecha de nacimiento debe estar entre ${ageLimits.minDate.format('DD/MM/YYYY')} y ${ageLimits.maxDate.format('DD/MM/YYYY')}.`
        )
      );
    }
    return Promise.resolve();
  }, [ageLimits.maxDate, ageLimits.minDate]);

  const isBirthDateDisabled = false;

  useEffect(() => {
    getDocumentTypeList();
    getGenderList();
  }, [getDocumentTypeList, getGenderList]);

  useEffect(() => {
    if (!insurancePolicyDetail?.articulo || !insurancePolicyDetail?.rama || !policyProductCode) {
      setApActivitiesListOptions([]);
      setAgeRanges([]);
      return;
    }
    getAPActiviesListPerProduct(
      insurancePolicyDetail.articulo,
      insurancePolicyDetail.rama,
      policyProductCode
    );
    getAgeRangesPerProduct(
      insurancePolicyDetail.articulo,
      insurancePolicyDetail.rama,
      policyProductCode
    );
  }, [insurancePolicyDetail?.articulo, insurancePolicyDetail?.rama, policyProductCode, getAgeRangesPerProduct]);

  return (
    <Form
      formProps={{
        form,
        onFinish,
        name: "insured-registration-form",
        className: "insured-registration-form-container",
      }}
    >
      <RowSize rowProps={{ gutter: [0, 0] }}>
        <ColSize
          size={100}
          colProps={{ lg: { span: 24 }, xl: { span: 8 } }}
          className="col-registration-item"
        >
          <InputForm
            formProps={{
              label: "Apellido y Nombre:",
              name: "fullName",
              rules: validateForm.generateRules({
                label: "apellido y nombre",
                rules: ["required"],
              }),
            }}
            inputProps={{
              placeholder: "Apellido y Nombre",
              onChange: () => setTimeout(validateFormComplete, 100),
            }}
          />
        </ColSize>

        <ColSize
          size={100}
          colProps={{ lg: { span: 24 }, xl: { span: 8 } }}
          className="col-registration-item"
        >
          <InputForm
            formProps={{
              label: "CUIL:",
              name: "cuil",
              rules: validateForm.generateRules({
                label: "cuil",
                rules: ["cuil"],
              }),
            }}
            inputProps={{
              placeholder: "CUIL",
              onChange: (e) => handleCuilChange(e.target.value),
            }}
          />
        </ColSize>

        <ColSize
          size={100}
          colProps={{ lg: { span: 24 }, xl: { span: 8 } }}
          className="col-registration-item"
        >
          <SelectForm
            formProps={{
              label: "Tipo de Documento:",
              name: "documentType",
              rules: validateForm.generateRules({
                label: "tipo de documento",
                rules: ["required"],
              }),
            }}
            selectProps={{
              disabled: !documentTypeList?.length,
              loading: isLoadingDocumentTypeList,
              options: documentTypeList,
              allowClear: true,
              labelInValue: true,
              onChange: (option) => {
                form?.setFieldValue('documentType', option.value);
                setTimeout(validateFormComplete, 100);
              },
            }}
          />
        </ColSize>

        <ColSize
          size={100}
          colProps={{ lg: { span: 24 }, xl: { span: 8 } }}
          className="col-registration-item"
        >
          <InputForm
            formProps={{
              label: "Numero de Documento:",
              name: "documentNumber",
              rules: validateForm.generateRules({
                label: "numero de documento",
                rules: ["required"],
              }),
            }}
            inputProps={{
              placeholder: "Nro. de Documento",
              onChange: () => setTimeout(validateFormComplete, 100),
            }}
          />
        </ColSize>

        <ColSize
          size={100}
          colProps={{ lg: { span: 24 }, xl: { span: 8 } }}
          className="col-registration-item"
        >
          <SelectForm
            formProps={{
              label: "Género:",
              name: "gender",
              rules: validateForm.generateRules({
                label: "género",
                rules: ["required"],
              }),
            }}
            selectProps={{
              disabled: !genderList?.length,
              loading: isLoadingGenderList,
              options: genderList,
              allowClear: true,
              labelInValue: true,
              onChange: (option) => {
                form?.setFieldValue('gender', option.value);
                setTimeout(validateFormComplete, 100);
              },
            }}
          />
        </ColSize>

        <ColSize
          size={100}
          colProps={{ lg: { span: 24 }, xl: { span: 8 } }}
          className="col-registration-item"
        >
          <DatePickerForm
            formProps={{
              label: "Fecha de Nacimiento:",
              name: "birthDate",
              rules: [
                ...validateForm.generateRules({
                  label: "fecha de nacimiento",
                  rules: ["required", 'dateNotAfterToday'],
                }),
                { validator: validateBirthDateRange },
              ],
            }}
            datePickerProps={{
              disabled: isBirthDateDisabled,
              minDate: ageLimits.minDate,
              maxDate: ageLimits.maxDate,
              placeholder: "Fecha Nacimiento",
              onChange: () => setTimeout(validateFormComplete, 100),
            }}
          />
        </ColSize>

        <ColSize
          size={100}
          colProps={{ lg: { span: 24 }, xl: { span: 8 } }}
          className="col-registration-item"
        >
          <SelectForm
            formProps={{
              label: "Actividades:",
              name: "activities",
              rules: validateForm.generateRules({
                label: "actividades",
                rules: ["required"],
              }),
            }}
            selectProps={{
              disabled: !apActiviesListOptions?.length,
              options: apActiviesListOptions,
              allowClear: true,
              labelInValue: true,
              showSearch: true,
              onChange: (option) => {
                form?.setFieldValue('activities', option.value);
                setTimeout(validateFormComplete, 100);
              },
            }}
          />
        </ColSize>

        <ColSize size={100}>
          <FormAnt.Item label=" " name="">
            <RowSize rowProps={{ gutter: [0, 0] }}>
              <ActionButton
                label="Agregar"
                icon={<PlusOutlined />}
                onClick={() => { }}
                style={{ marginRight: 15, width: 165, height: 32 }}
                htmlType="submit"
                type={isFormComplete ? "primary" : "secondary"}
                disabled={disabledButtons}
                className={isFormComplete ? "btn-principal-barbuss" : "btn-principal-outline-barbuss"}
              />

              <ActionButton
                className="btn-principal-barbuss"
                label="Generar certificado"
                onClick={() => onGenerate()}
                style={{ width: 165, height: 32 }}
                disabled={disabledButtons}
              />
            </RowSize>
          </FormAnt.Item>
        </ColSize>
      </RowSize>
    </Form>
  );
};
